import { useState, useRef, useEffect } from 'react';
import { titleGeneratorAPI } from '../lib/api';
import { toast } from 'sonner';
import {
  Upload, Loader2, Copy, Sparkles, RefreshCw, X, FileImage, Check,
  Link as LinkIcon, Tag, Crown, Trophy, ClipboardPaste,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';

const VARIATION_ICONS = [Tag, Tag, Tag, Tag, Tag];

const PREVIEW_VARIATIONS = [
  { label: 'Best Purchase-Intent Title',     title: 'Oval Lab Grown Diamond Engagement Ring – Hidden Halo 14K Gold – IGI Certified' },
  { label: 'Best Shape-First Title',         title: 'Oval Lab Diamond Solitaire Ring – 14K White Gold – IGI Certified' },
  { label: 'Best Setting/Style-First Title', title: 'Hidden Halo Lab Diamond Engagement Ring – 14K Gold – Custom Made' },
  { label: 'Best Trust/Certification-First', title: 'IGI Certified Lab Diamond Engagement Ring – Oval 14K Gold – D/F VS1' },
  { label: 'Best Occasion/Gift-Based Title', title: 'Lab Diamond Anniversary Ring – Oval 14K Gold – Gift for Her' },
];

export default function TitleGenerator() {
  const fileInputRef = useRef(null);
  const [mode, setMode] = useState('upload');           // 'upload' | 'url' | 'text'
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [urlValue, setUrlValue] = useState('');
  const [textValue, setTextValue] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const pickFile = (f) => {
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      toast.error('Please upload an image file (PNG, JPG, WEBP, etc.)');
      return;
    }
    if (f.size > 20 * 1024 * 1024) {
      toast.error('Image too large (max 20 MB)');
      return;
    }
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
    setResult(null);
  };

  const clearAll = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(null);
    setPreviewUrl(null);
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleGenerate = async () => {
    if (mode === 'upload' && !file) { toast.error('Upload a CAD or product image first'); return; }
    if (mode === 'url' && !urlValue.trim()) { toast.error('Paste a URL first'); return; }
    if (mode === 'text' && !textValue.trim()) { toast.error('Paste the product details first'); return; }
    try {
      setAnalyzing(true);
      let res;
      if (mode === 'upload') res = await titleGeneratorAPI.analyzeCad(file);
      else if (mode === 'url') res = await titleGeneratorAPI.analyzeUrl(urlValue.trim());
      else res = await titleGeneratorAPI.analyzeText(textValue.trim());
      setResult(res.data);
      if (res.data.parse_failed) {
        toast.warning('AI returned an unstructured response — check the raw output.');
      } else {
        toast.success(`${res.data.variations?.length || 0} title variations generated`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleCopy = (text, idx) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(null), 1500);
    toast.success('Title copied');
  };

  const handleCopyAll = () => {
    if (!result?.variations?.length) return;
    const lines = [];
    if (result.best_title) lines.push(`★ Best: ${result.best_title}`, '');
    result.variations.forEach((v) => {
      lines.push(`${v.label}:`, v.title, '');
    });
    navigator.clipboard.writeText(lines.join('\n').trim());
    toast.success('All titles copied');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) pickFile(f);
  };

  return (
    <div className="space-y-6" data-testid="title-generator-page">
      {/* Hero */}
      <div className="bg-gradient-to-br from-amber-50 via-white to-zinc-50 border border-amber-100 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-amber-600 rounded-lg">
            <Tag size={16} className="text-white" />
          </div>
          <span className="text-xs uppercase tracking-widest text-amber-700 font-medium">
            AI Tool
          </span>
        </div>
        <h1 className="text-3xl font-semibold text-zinc-900 tracking-tight">Title Generator</h1>
        <p className="text-sm text-zinc-600 mt-2 max-w-2xl">
          Drop a CAD render, product image, or paste any product URL — Claude reads the design and
          writes 5 buyer-intent-led Etsy titles, then picks the strongest one with reasoning.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Source */}
        <div className="bg-white border border-zinc-200 rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-zinc-900">Source</h3>
            {(file || urlValue || textValue) && (
              <button
                onClick={() => { clearAll(); setUrlValue(''); setTextValue(''); }}
                className="text-xs text-zinc-500 hover:text-red-600 inline-flex items-center gap-1"
                data-testid="clear-source-btn"
              >
                <X size={12} /> Clear
              </button>
            )}
          </div>

          {/* Mode toggle */}
          <div className="flex items-center gap-1 bg-zinc-100 p-1 rounded-lg">
            <button
              onClick={() => { setMode('upload'); setResult(null); }}
              className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                mode === 'upload' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-800'
              }`}
              data-testid="title-mode-upload"
            >
              <Upload size={12} /> Upload image
            </button>
            <button
              onClick={() => { setMode('url'); setResult(null); }}
              className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                mode === 'url' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-800'
              }`}
              data-testid="title-mode-url"
            >
              <LinkIcon size={12} /> From URL
            </button>
            <button
              onClick={() => { setMode('text'); setResult(null); }}
              className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                mode === 'text' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-800'
              }`}
              data-testid="title-mode-text"
            >
              <ClipboardPaste size={12} /> Paste Text
            </button>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => pickFile(e.target.files?.[0])}
            data-testid="title-file-input"
          />

          {mode === 'text' ? (
            <div className="space-y-3">
              <div>
                <Textarea
                  value={textValue}
                  onChange={(e) => setTextValue(e.target.value)}
                  placeholder={"Paste the product title, description, and any specs here. Works great when a site (Etsy, Amazon) blocks our URL fetch.\n\nExample:\n2.5ct Round Lab Grown Diamond Engagement Ring, IGI Certified, 14K White Gold, Hidden Halo, D color VS1 clarity..."}
                  rows={10}
                  className="text-sm bg-white border-zinc-200 text-zinc-900 resize-none font-mono leading-relaxed"
                  data-testid="title-text-input"
                />
                <p className="text-[11px] text-zinc-500 mt-1.5">
                  Copy product info directly from the page (title + description + specs) and paste it here.
                  Bypasses scraping entirely.
                </p>
              </div>
              <Button
                onClick={handleGenerate}
                disabled={analyzing || !textValue.trim()}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="title-generate-btn"
              >
                {analyzing ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Generating titles…</>
                 : result ? <><RefreshCw size={14} className="mr-1.5" /> Regenerate titles</>
                 : <><Sparkles size={14} className="mr-1.5" /> Generate titles from text</>}
              </Button>
            </div>
          ) : mode === 'url' ? (
            <div className="space-y-3">
              <div>
                <Input
                  value={urlValue}
                  onChange={(e) => setUrlValue(e.target.value)}
                  placeholder="https://example.com/product/... or direct image URL"
                  className="h-10 text-sm"
                  onKeyDown={(e) => { if (e.key === 'Enter' && !analyzing) handleGenerate(); }}
                  data-testid="title-url-input"
                />
                <p className="text-[11px] text-zinc-500 mt-1.5">
                  Works for most shop pages. If a site blocks us (Etsy, Amazon often do), switch to <strong>Paste Text</strong>.
                </p>
              </div>
              <Button
                onClick={handleGenerate}
                disabled={analyzing || !urlValue.trim()}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="title-generate-btn"
              >
                {analyzing ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Analyzing URL…</>
                 : result ? <><RefreshCw size={14} className="mr-1.5" /> Regenerate titles</>
                 : <><Sparkles size={14} className="mr-1.5" /> Generate titles from URL</>}
              </Button>
            </div>
          ) : previewUrl ? (
            <div className="space-y-3">
              <div className="relative aspect-square bg-zinc-50 rounded-lg overflow-hidden border border-zinc-200">
                <img src={previewUrl} alt="Preview" className="w-full h-full object-contain" data-testid="title-preview" />
              </div>
              <div className="flex items-center justify-between text-xs text-zinc-500">
                <span className="truncate pr-2">{file?.name}</span>
                <span className="shrink-0">{(file?.size / 1024).toFixed(0)} KB</span>
              </div>
              <Button
                onClick={handleGenerate}
                disabled={analyzing}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="title-generate-btn"
              >
                {analyzing ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Analyzing with AI…</>
                 : result ? <><RefreshCw size={14} className="mr-1.5" /> Regenerate titles</>
                 : <><Sparkles size={14} className="mr-1.5" /> Generate titles</>}
              </Button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              className={`w-full aspect-square border-2 border-dashed rounded-lg flex flex-col items-center justify-center gap-2 transition-colors cursor-pointer ${
                dragging ? 'border-amber-400 bg-amber-50' : 'border-zinc-200 bg-zinc-50 hover:border-zinc-300 hover:bg-zinc-100'
              }`}
              data-testid="title-upload-dropzone"
            >
              <div className="p-3 bg-white rounded-full shadow-sm">
                <FileImage size={20} className="text-zinc-500" />
              </div>
              <p className="text-sm font-medium text-zinc-700">Drop CAD or product image</p>
              <p className="text-xs text-zinc-500">PNG, JPG, WEBP · max 20 MB</p>
              <div className="inline-flex items-center gap-1 mt-2 px-3 py-1 bg-zinc-900 text-white text-xs rounded-full">
                <Upload size={11} /> Select image
              </div>
            </button>
          )}
        </div>

        {/* Result */}
        <div className="bg-white border border-zinc-200 rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-zinc-900">Generated Titles</h3>
            {result?.variations?.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs border-zinc-200"
                onClick={handleCopyAll}
                data-testid="copy-all-titles-btn"
              >
                <Copy size={12} className="mr-1" /> Copy all
              </Button>
            )}
          </div>

          {analyzing ? (
            <div className="aspect-square bg-gradient-to-br from-zinc-50 to-amber-50/30 rounded-lg flex flex-col items-center justify-center gap-3">
              <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
              <p className="text-xs text-zinc-600">Claude is reading the product…</p>
              <p className="text-[11px] text-zinc-400">Usually takes 15–30 seconds</p>
            </div>
          ) : result?.variations?.length > 0 ? (
            <div className="space-y-3" data-testid="title-result-block">
              {/* Best title */}
              {result.best_title && (
                <div
                  className="bg-gradient-to-br from-amber-50 to-amber-100/50 border border-amber-200 rounded-lg p-4"
                  data-testid="best-title-block"
                >
                  <div className="flex items-center gap-1.5 mb-2">
                    <Trophy size={14} className="text-amber-700" />
                    <span className="text-[11px] uppercase tracking-wider font-semibold text-amber-800">
                      Recommended best title
                    </span>
                  </div>
                  <p className="text-sm font-semibold text-zinc-900 leading-snug" data-testid="best-title-text">
                    {result.best_title}
                  </p>
                  <div className="flex items-center justify-between mt-2 gap-3">
                    <p className="text-[11px] text-amber-900/80 italic flex-1" data-testid="best-title-reason">
                      {result.best_title_reason}
                    </p>
                    <button
                      onClick={() => handleCopy(result.best_title, 'best')}
                      className="text-amber-700 hover:text-amber-900 flex-shrink-0"
                      title="Copy best title"
                      data-testid="copy-best-title-btn"
                    >
                      {copiedIdx === 'best' ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
                    </button>
                  </div>
                  <div className="mt-2 text-[10px] text-amber-700/60 font-mono">
                    {result.best_title.length} chars
                  </div>
                </div>
              )}

              {/* Variations list */}
              <div className="space-y-2">
                {result.variations.map((v, i) => {
                  const Icon = VARIATION_ICONS[i] || Tag;
                  const isBest = v.title === result.best_title;
                  return (
                    <div
                      key={i}
                      className={`group rounded-lg border p-3 transition-colors ${
                        isBest ? 'bg-amber-50/50 border-amber-200' : 'bg-zinc-50 border-zinc-100 hover:border-zinc-200'
                      }`}
                      data-testid={`title-variation-${i}`}
                    >
                      <div className="flex items-center gap-2 mb-1.5">
                        <Icon size={11} className={isBest ? 'text-amber-700' : 'text-zinc-400'} />
                        <span className={`text-[10px] uppercase tracking-wider font-semibold ${
                          isBest ? 'text-amber-800' : 'text-zinc-500'
                        }`}>
                          {v.label}
                        </span>
                        {isBest && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-600 text-white text-[9px] uppercase tracking-wider rounded">
                            <Crown size={9} /> Pick
                          </span>
                        )}
                      </div>
                      <div className="flex items-start justify-between gap-3">
                        <p className="text-sm text-zinc-900 leading-snug flex-1">{v.title}</p>
                        <button
                          onClick={() => handleCopy(v.title, i)}
                          className="text-zinc-400 hover:text-zinc-900 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-0.5"
                          title="Copy title"
                          data-testid={`copy-variation-${i}-btn`}
                        >
                          {copiedIdx === i ? <Check size={13} className="text-emerald-600 opacity-100" /> : <Copy size={13} />}
                        </button>
                      </div>
                      <div className="mt-1 text-[10px] text-zinc-400 font-mono">
                        {v.title.length} chars
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="text-[11px] text-zinc-400 flex items-center gap-1 pt-1">
                <Sparkles size={10} /> Tip: hover any variation to copy. Click "Regenerate" to try a different read.
              </div>
            </div>
          ) : result?.parse_failed ? (
            <div className="space-y-2">
              <p className="text-xs text-amber-700">
                AI returned an unstructured response — showing the raw output below:
              </p>
              <div className="text-xs text-zinc-700 font-mono whitespace-pre-wrap bg-zinc-50 border border-zinc-200 rounded p-3 max-h-96 overflow-y-auto" data-testid="title-raw-output">
                {result.raw}
              </div>
            </div>
          ) : (
            <div className="aspect-square bg-zinc-50 border border-dashed border-zinc-200 rounded-lg flex flex-col items-center justify-center gap-3 p-6">
              <p className="text-sm text-zinc-500 font-medium">Preview format</p>
              <div className="w-full space-y-2 max-h-72 overflow-auto">
                {PREVIEW_VARIATIONS.map((v, i) => (
                  <div key={i} className="bg-white border border-zinc-100 rounded p-2.5">
                    <div className="text-[9px] uppercase tracking-wider font-semibold text-zinc-400 mb-1">{v.label}</div>
                    <p className="text-xs text-zinc-700 leading-snug">{v.title}</p>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-zinc-400 text-center">
                Upload a CAD or paste a URL to generate your version.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
